import { apiRequest } from "../../api/apiClient";
import type { Setting, UpdateSettingDto } from "../../types/settings.types";

interface SettingResponse {
  key?: string;
  value?: string;
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

const tryGetSetting = async (path: string): Promise<SettingResponse | null> => {
  const response = await apiRequest(path, { method: "GET" });
  if (!response.ok) {
    return null;
  }
  return handleResponse<SettingResponse>(response);
};

export const settingsService = {
  getSettings: async (): Promise<Setting[]> => {
    const response = await apiRequest("/Settings", { method: "GET" });
    return handleResponse<Setting[]>(response);
  },

  getSettingByKey: async (key: string): Promise<SettingResponse | null> => {
    const byKey = await tryGetSetting(`/Settings/key/${key}`);
    if (byKey) {
      return byKey;
    }
    const direct = await tryGetSetting(`/Settings/${key}`);
    if (direct) {
      return direct;
    }
    throw new Error("Failed to load setting");
  },

  batchUpdateSettings: async (settings: UpdateSettingDto[]): Promise<void> => {
    for (const setting of settings) {
      // Try PUT to update existing setting by key
      let response = await apiRequest(`/Settings/key/${setting.key}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(setting),
      });

      // If not found (404/405), try creating with POST
      if (
        !response.ok &&
        (response.status === 404 || response.status === 405)
      ) {
        response = await apiRequest("/Settings", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(setting),
        });
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(
          error.message || `Failed to update setting: ${setting.key}`,
        );
      }
    }
  },
};
